import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { AppAlert } from '../../services/AppAlert';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import Typography from '../../components/Reusables/Typography';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { Picker } from '@react-native-picker/picker';
import { usePickerItemColor } from '../../hooks/usePickerItemColor';
import { useFocusEffect } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import {
  updateBusinessInfo,
  updateBusinessValidation,
} from '../../redux/AuthSlice';
import { useAppSelector } from '../../redux/helper';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { getEmployeeProfileDetails } from '../../api/employees';
import {
  addInAppEmployeeInfo,
  addInAppBusinessInfo,
  clearEmployeeAndBusinessInfo,
} from '../../redux/AuthSlice';
import store, { logoutAction, persistor } from '../../redux/Store';
import type { AppDispatch } from '../../redux/Store';
import { completeOwnerSetupHandoff } from './ownerSetupBusinessCompletion';
import {
  clearOwnerSetupHandoffMarker,
  writeOwnerSetupHandoffMarker,
} from '../../services/ownerSetupSessionHandoff';
import {
  preloadEmployeeSession,
  preloadOwnerSession,
} from '../../services/postLoginPreload';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import useBackofficeRefresh from '../../hooks/useBackofficeRefresh';
import { setEmployeeEmail } from '../../services/SecureCredentials';
import { dumpTokens, s, fs, iconSize, v, borderWidth } from '../../utils/libs';
import { GTFonts, theme } from '../../theme';
import { AuthContext } from '../../Context/AuthContext';
import ErrorContext from '../../Context/ErrorContext';
import { BUSINESS_TYPES } from '../../CONSTANTS';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import StockExpenseSettingModal from '../../components/Modals/StockExpenseSettingModal';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import { buildAppAnalyticsIdentify } from '../../services/appAnalyticsIdentify';

const BusinessDetailsScreen = ({ navigation }: { navigation: any }) => {
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const dispatch = useDispatch();
  const axiosprivate = useAxiosPrivate();
  const axiosBackoffice = useBackofficeAxios();
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const { clearOwnerSession } = useBackofficeRefresh();
  const { setAuth } = useAuth();
  const { businessInfo, formValidation } = useAppSelector(
    state => state.auth.register,
  );
  const { handleError } = useContext(ErrorContext);
  const { setTokenAssistant } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [stockExpenseModalVisible, setStockExpenseModalVisible] =
    useState(false);
  const [autoCreateStockExpense, setAutoCreateStockExpense] = useState(true);
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [touchedFields, setTouchedFields] = useState<
    Partial<Record<keyof typeof businessInfo, boolean>>
  >({});

  const nameInputRef = useRef<TextInput>(null);

  useFocusEffect(
    useCallback(() => {
      const timer = setTimeout(() => {
        nameInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }, []),
  );

  const validateField = (field: keyof typeof businessInfo, value: string) => {
    let isValid = false;
    switch (field) {
      case 'name':
        isValid = value.trim() !== '';
        dispatch(updateBusinessValidation({ validBusinessName: isValid }));
        break;
      case 'type':
        isValid = value.trim() !== '';
        dispatch(updateBusinessValidation({ validBusinessType: isValid }));
        break;
      case 'county':
      case 'town':
        isValid = true;
        dispatch(
          updateBusinessValidation({
            validCounty: true,
            validTown: true,
          }),
        );
        break;
    }
    return isValid;
  };

  const handleBlur = (field: keyof typeof businessInfo) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    validateField(field, businessInfo[field]);
  };

  const handleChange = (field: keyof typeof businessInfo, value: string) => {
    dispatch(updateBusinessInfo({ [field]: value }));
    if (!touchedFields[field]) {
      setTouchedFields(prev => ({ ...prev, [field]: true }));
    }
    validateField(field, value);
  };

  const isFormValid = useMemo(() => {
    return formValidation.validBusinessName && formValidation.validBusinessType;
  }, [formValidation]);

  const handleSubmit = async () => {
    if (!isFormValid) return;
    if (loading) return;
    setLoading(true);
    const accessToken = await AsyncStorage.getItem('accessToken');
    const userId = await AsyncStorage.getItem('userId');
    console.log('PRESUBMIT', userId, accessToken, businessInfo);
    const ctx = buildAppAnalyticsContext();
    try {
      const response = await axiosprivate.patch(
        `bp_update_business_information`,
        {
          bp_business_name: businessInfo.name,
          bp_business_type: businessInfo.type,
          bp_business_category: '',
          bp_business_duration: '',
          bp_total_employees: '',
          bp_business_description: businessInfo.description,
          bp_country: 'Kenya',
          bp_county: (businessInfo.county ?? '').trim(),
          bp_town: (businessInfo.town ?? '').trim(),
          bp_postal_code: '',
          bp_location_auto_create_stock_expense: autoCreateStockExpense,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      console.log('bp_update_business_information', response);

      if (response?.data?.status === 200) {
        appAnalytics.track('acq_business_details_succeeded', { ...ctx });
        await handleOwnerToEmployee(response?.data?.data?._id);
      } else {
        appAnalytics.track('acq_business_details_failed', {
          ...ctx,
          error_category: 'server',
        });
      }
    } catch (error: any) {
      console.error('Error in handleSubmit:', error, error.message);
      appAnalytics.track('acq_business_details_failed', {
        ...ctx,
        error_category: error?.response ? 'server' : 'network',
      });
      AppAlert.alert(
        'Error',
        'Failed to save business details. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerToEmployee = async (id: string) => {
    setLoading(true);
    const accessToken = await AsyncStorage.getItem('accessToken');
    if (!accessToken) {
      console.log('NO TOKEN', accessToken);
      setLoading(false);
      return;
    }
    try {
      const response = await axiosprivate.get(
        `/bp_owner_to_employee_information?bp_owner_id=${id}`,
        {
          headers: { Authorization: 'Bearer ' + accessToken },
        },
      );

      console.log('bp_owner_to_employee_information1', response.data);

      if (response?.data?.status === 200) {
        const newAccessToken = response.data?.accessToken;
        const newRefreshToken = response.data?.refreshToken;
        if (newAccessToken && newRefreshToken) {
          await runOwnerSetupHandoff(id, newAccessToken, newRefreshToken);
        }
      }
    } catch (error) {
      console.error('Error in handleOwnerToEmployee:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Durable owner-conversion handoff (US1): batch-write tokens + marker,
   * flush pending owner-setup eligibility, then apply the employee profile
   * and only afterwards flip Auth→App so routing lands on the wizard.
   */
  const runOwnerSetupHandoff = async (
    ownerId: string,
    accessToken: string,
    refreshToken: string,
  ) => {
    let profileEmail = '';
    const outcome = await completeOwnerSetupHandoff(
      {
        dispatch,
        persistTokens: async (access, refresh) => {
          await AsyncStorage.multiSet([
            ['accessToken', access],
            ['refreshToken', refresh],
          ]);
        },
        writeHandoffMarker: writeOwnerSetupHandoffMarker,
        clearHandoffMarker: clearOwnerSetupHandoffMarker,
        flushPersistor: () => persistor.flush(),
        fetchEmployeeProfile: token =>
          getEmployeeProfileDetails(axiosprivate, { accessToken: token }),
        applyEmployeeProfile: profileResponse => {
          const data = profileResponse?.data?.data;
          profileEmail = String(
            data?.bp_employee_email_address || '',
          ).trim();
          dispatch(addInAppEmployeeInfo(data));
          dispatch(
            addInAppBusinessInfo({
              name: profileResponse?.data?.businessInformation
                ?.bp_user_business_name,
              description:
                profileResponse?.data?.businessInformation
                  ?.bp_user_business_description,
              Adrress: profileResponse?.data?.addressInformation,
              type: profileResponse?.data?.businessInformation
                ?.bp_user_business_type,
            }),
          );
          return String(data?._id || '');
        },
        preloadSession: async (token, employeeId, setupOwnerId) => {
          // Same sequence as LoginScreen post-login preload for owners.
          const locationAfterPreload = await preloadEmployeeSession({
            dispatch: dispatch as AppDispatch,
            axiosPrivate: axiosprivate,
            accessToken: token,
            employeeId,
          });

          if (profileEmail) {
            await setEmployeeEmail(profileEmail);
          }

          // Drop any previous account's owner JWT so autoLoginOwner cannot
          // short-circuit with a mismatched token + this location.
          await clearOwnerSession();
          const ownerResult = await autoLoginOwner();
          if (
            ownerResult &&
            'accessToken' in ownerResult &&
            ownerResult.accessToken
          ) {
            await preloadOwnerSession({
              dispatch: dispatch as AppDispatch,
              axiosBackoffice,
              ownerAccessToken: ownerResult.accessToken,
              ownerId: setupOwnerId,
              currentLocationId: locationAfterPreload.currentLocationId,
            });
          }
        },
        setAuthTokens: (access, refresh) => {
          setTokenAssistant({ accessToken: access, refreshToken: refresh });
          setAuth({ accessToken: access, refreshToken: refresh });
        },
        now: () => new Date().toISOString(),
      },
      { ownerId, accessToken, refreshToken },
    );

    if (outcome.kind === 'ready') {
      try {
        const state = store.getState();
        const employee = state.auth.inApp.account.employeeInfo;
        const location = state.location.currentLocation;
        const business = state.auth.inApp.account.businessInfo;
        const locationType =
          location?.bp_location_is_system_generated === true
            ? 'test'
            : location
              ? 'real'
              : undefined;
        const identify = buildAppAnalyticsIdentify({
          employee,
          location: location
            ? {
                ...location,
                bp_location_type: locationType ?? location.bp_location_type,
              }
            : location,
          business,
        });
        if (identify) appAnalytics.identify(identify);
      } catch (e) {
        console.log('app analytics business identify skipped', e);
      }
      return;
    }
    if (outcome.kind === 'requires_reauthentication') {
      handleError(
        'Your session has expired. You will be be prompted to login again with the new credentials.',
        'error',
        true,
      );
      setTimeout(() => {
        clearAll();
      }, 4000);
      return;
    }
    if (outcome.kind === 'employee_missing') {
      await clearAll();
      return;
    }
    if (outcome.kind === 'profile_error') {
      // Eligibility + handoff marker are already durable; session bootstrap
      // finishes the recovery on next launch, but offer an immediate retry.
      const retry = await AppAlert.confirm(
        'Connection problem',
        'Your account was created, but we could not finish setting up your session. Retry now?',
        { okText: 'Retry', cancelText: 'Later' },
      );
      if (retry) {
        await runOwnerSetupHandoff(ownerId, accessToken, refreshToken);
      }
    }
  };

  const clearAll = async () => {
    dumpTokens();
    setAuth({ accessToken: '', refreshToken: '' });
    // setTokenAssistant({ accessToken: '', refreshToken: '' }); -test if it affects anything-NOTIME NOW
    dispatch(clearEmployeeAndBusinessInfo());
    await AsyncStorage.removeItem('refreshToken');
    await AsyncStorage.removeItem('accessToken');
    dispatch(logoutAction());
    navigation.navigate('Login');
  };

  const isWebLarge = useWebLikeLayout && isLargeScreen;

  return (
    <View style={styles.container}>
      <KeyboardAwareFormScroll
        style={styles.sheetContent}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        bottomOffset={v(200)}>
        <View style={styles.formCard}>
          {isWebLarge ? (
            <View>
              <Typography style={styles.webWelcomeText}>
                Almost there!
              </Typography>
              <Typography style={styles.webTitle}>Business Details</Typography>
            </View>
          ) : null}
          <Typography style={styles.subHeader}>
            Enter Your Business Details. Some of the details will appear on your receipts.
          </Typography>
          <Typography style={styles.sectionTitle}>
            Business Information
          </Typography>
          <View style={styles.inputContainer}>
            <Typography style={styles.pseudoLabel}>
              Business Name <Text style={styles.requiredStar}>*</Text>
            </Typography>
            <View
              style={[
                styles.fieldBox,
                touchedFields.name &&
                  !formValidation.validBusinessName &&
                  styles.errorInput,
              ]}>
              <TextInput
                style={styles.textInput}
                ref={nameInputRef}
                autoFocus
                returnKeyType="next"
                placeholder="Enter business name"
                placeholderTextColor={theme.colors.textTertiary}
                value={businessInfo.name}
                onChangeText={(text: string) =>
                  handleChange(
                    'name',
                    text.replace(/[^A-Za-z0-9\s&'.,!?-]/g, ''),
                  )
                }
                onBlur={() => handleBlur('name')}
              />
            </View>
            {touchedFields.name && !formValidation.validBusinessName && (
              <Typography style={styles.errorText}>
                Business name is required
              </Typography>
            )}
          </View>
          <View style={styles.inputContainer}>
            <Typography style={styles.pseudoLabel}>
              Business Type <Text style={styles.requiredStar}>*</Text>
            </Typography>
            <View
              style={[
                styles.pickerContainer,
                touchedFields.type &&
                  !formValidation.validBusinessType &&
                  styles.errorInput,
              ]}>
              <Picker
                selectedValue={businessInfo.type || ''}
                onValueChange={(itemValue: string) => {
                  setTouchedFields(prev => ({ ...prev, type: true }));
                  handleChange('type', itemValue);
                  validateField('type', itemValue);
                }}
                mode="dropdown"
                dropdownIconColor={theme.colors.text}
                style={styles.picker}>
                <Picker.Item
                  label="Select business type"
                  value=""
                  color={pickerItemColorSecondary}
                />
                {BUSINESS_TYPES.map((bt: string) => (
                  <Picker.Item
                    key={bt}
                    label={bt}
                    value={bt}
                    color={pickerItemColor}
                  />
                ))}
              </Picker>
            </View>
            {touchedFields.type && !formValidation.validBusinessType && (
              <Typography style={styles.errorText}>
                Business type is required
              </Typography>
            )}
          </View>
          <View style={styles.inputContainer}>
            <Typography style={styles.pseudoLabel}>
              Stock Expense Tracking <Text style={styles.requiredStar}>*</Text>
            </Typography>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={() => setStockExpenseModalVisible(true)}
              style={styles.selectInput}>
              <Typography style={styles.selectInputText}>
                {autoCreateStockExpense
                  ? 'Record expenses automatically'
                  : 'Handle expenses separately'}
              </Typography>
              <Ionicons
                name="chevron-down"
                size={iconSize(14)}
                color={theme.colors.textSecondary}
              />
            </TouchableOpacity>
          </View>
          <View style={styles.inputContainer}>
            <Typography style={styles.pseudoLabel}>
              Business Description
            </Typography>
            <View style={styles.fieldBox}>
              <TextInput
                style={[styles.textInput, styles.textInputMultiline]}
                placeholder="Brief description of your business"
                value={businessInfo.description}
                placeholderTextColor={theme.colors.textTertiary}
                onChangeText={(text: string) =>
                  handleChange('description', text)
                }
                multiline
                numberOfLines={3}
              />
            </View>
          </View>
          <View style={styles.accordionSection}>
            <TouchableOpacity
              onPress={() => setIsAddressExpanded(prev => !prev)}
              activeOpacity={0.7}
              style={styles.sectionHeaderRow}>
              <Typography style={styles.accordionTitle}>
                Business Address
              </Typography>
              <Ionicons
                name={isAddressExpanded ? 'chevron-up' : 'chevron-down'}
                size={iconSize(16)}
                color={theme.colors.text}
              />
            </TouchableOpacity>
            {isAddressExpanded ? (
              <View style={styles.accordionContent}>
                <View style={styles.inputContainer}>
                  <Typography style={styles.pseudoLabel}>
                    County/State
                  </Typography>
                  <View style={styles.fieldBox}>
                    <TextInput
                      style={styles.textInput}
                      placeholder="Enter county/state (optional)"
                      value={businessInfo.county}
                      placeholderTextColor={theme.colors.textTertiary}
                      onChangeText={(text: string) =>
                        handleChange('county', text)
                      }
                      onBlur={() => handleBlur('county')}
                    />
                  </View>
                </View>
                <View style={styles.inputContainer}>
                  <Typography style={styles.pseudoLabel}>Town/City</Typography>
                  <View style={styles.fieldBox}>
                    <TextInput
                      style={styles.textInput}
                      placeholder="Enter town/city (optional)"
                      value={businessInfo.town}
                      placeholderTextColor={theme.colors.textTertiary}
                      onChangeText={(text: string) =>
                        handleChange('town', text)
                      }
                      onBlur={() => handleBlur('town')}
                      onKeyPress={(e: any) => {
                        const key = e?.nativeEvent?.key ?? e?.key;
                        if (key !== 'Enter') return;
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        if (loading || !isFormValid) return;
                        void handleSubmit();
                      }}
                      returnKeyType="done"
                      onSubmitEditing={() => {
                        if (loading || !isFormValid) return;
                        void handleSubmit();
                      }}
                    />
                  </View>
                </View>
              </View>
            ) : null}
          </View>
          <Typography style={styles.infoText}>
            You can add more business outlets/locations later
          </Typography>
          {isWebLarge ? (
            <TouchableOpacity
              style={[
                styles.submitButton,
                (loading || !isFormValid) && styles.disabledButton,
              ]}
              onPress={handleSubmit}
              disabled={loading || !isFormValid}>
              {loading ? (
                <ActivityIndicator color={theme.colors.textInverse} />
              ) : (
                <View style={styles.buttonContent}>
                  <Ionicons
                    name="checkmark-circle"
                    size={20}
                    color={theme.colors.textInverse}
                  />
                  <Typography style={styles.buttonText}>
                    Complete Setup
                  </Typography>
                </View>
              )}
            </TouchableOpacity>
          ) : null}
        </View>
      </KeyboardAwareFormScroll>
      <StockExpenseSettingModal
        visible={stockExpenseModalVisible}
        onClose={() => setStockExpenseModalVisible(false)}
        currentValue={autoCreateStockExpense}
        onConfirm={setAutoCreateStockExpense}
      />
      {!isWebLarge ? (
        <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
          <View style={styles.stickyButtonContainer}>
            <TouchableOpacity
              style={[
                styles.submitButton,
                loading || !isFormValid ? styles.disabledButton : null,
              ]}
              onPress={handleSubmit}
              disabled={loading || !isFormValid}>
              {loading ? (
                <ActivityIndicator color={theme.colors.textInverse} />
              ) : (
                <View style={styles.buttonContent}>
                  <Ionicons
                    name="checkmark-circle"
                    size={iconSize(20)}
                    color={theme.colors.textInverse}
                  />
                  <Typography style={styles.buttonText}>Done</Typography>
                </View>
              )}
            </TouchableOpacity>
          </View>
        </KeyboardStickyView>
      ) : null}
    </View>
  );
};

const fieldBorder = {
  borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
  borderColor: theme.colors.borderGray,
  borderRadius: s(8),
  backgroundColor: theme.colors.surface,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  sheetContent: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContainer: {
    flexGrow: 1,
    padding: s(8),
    paddingBottom: v(20),
    gap: v(12),
    ...(Platform.OS === 'web' && {
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      alignSelf: 'center',
      width: '100%',
      padding: s(24),
      gap: v(20),
    }),
  },
  formCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    padding: s(12),
    gap: v(16),
    ...theme.shadows.sm,
    ...(Platform.OS === 'web' && {
      padding: s(24),
      gap: v(20),
    }),
  },
  webWelcomeText: {
    fontSize: fs(14),
    lineHeight: fs(18),
    color: theme.colors.primaryDark,
    fontFamily: GTFonts.semibold,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: v(8),
  },
  webTitle: {
    fontSize: fs(28),
    lineHeight: fs(34),
    fontWeight: '700',
    color: theme.colors.textGray900,
    fontFamily: GTFonts.semibold,
  },
  subHeader: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.graphiteDark,
  },
  sectionTitle: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(16),
    fontWeight: '700',
    lineHeight: fs(18),
    color: theme.colors.textGray900,
    marginTop: v(4),
    ...(Platform.OS === 'web' && {
      fontSize: fs(18),
      marginTop: v(8),
      paddingBottom: v(8),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderGray,
    }),
  },
  inputContainer: {
    gap: v(8),
  },
  pseudoLabel: {
    fontSize: fs(14),
    color: theme.colors.textGray600,
    lineHeight: fs(18),
    fontFamily: GTFonts.semibold,
  },
  requiredStar: {
    color: theme.colors.error,
  },
  fieldBox: {
    ...fieldBorder,
    overflow: 'hidden',
  },
  textInput: {
    height: v(50),
    paddingHorizontal: s(12),
    fontSize: fs(16),
    fontFamily: GTFonts.regular,
    color: theme.colors.textGray900,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      outlineWidth: 0,
    }),
  } as any,
  textInputMultiline: {
    height: v(90),
    paddingTop: v(12),
    textAlignVertical: 'top',
  },
  pickerContainer: {
    ...fieldBorder,
    overflow: 'hidden',
  },
  picker: {
    fontSize: fs(16),
    fontFamily: GTFonts.regular,
    lineHeight: fs(20),
    color: theme.colors.shadow,
    ...(Platform.OS === 'web' && {
      borderWidth: 0,
      backgroundColor: 'transparent',
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
      paddingHorizontal: 12,
    }),
  } as any,
  selectInput: {
    ...fieldBorder,
    height: v(50),
    paddingLeft: s(12),
    paddingRight: s(12),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectInputText: {
    fontSize: fs(16),
    color: theme.colors.textGray900,
    flex: 1,
  },
  accordionSection: {
    marginTop: v(4),
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...(Platform.OS === 'web' && {
      paddingBottom: v(8),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderGray,
    }),
  },
  accordionTitle: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(16),
    fontWeight: '700',
    lineHeight: fs(18),
    color: theme.colors.textGray900,
    ...(Platform.OS === 'web' && {
      fontSize: fs(18),
    }),
  },
  accordionContent: {
    paddingTop: v(16),
    gap: v(16),
    ...(Platform.OS === 'web' && {
      gap: v(20),
    }),
  },
  errorInput: {
    borderColor: theme.colors.error,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: fs(12),
    marginTop: v(4),
  },
  infoText: {
    fontFamily: GTFonts.regular,
    fontStyle: 'italic',
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.graphiteDark,
  },
  submitButton: {
    backgroundColor: theme.colors.primaryDark,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: s(12),
    paddingVertical: s(14),
    width: '100%',
  },
  disabledButton: {
    backgroundColor: theme.colors.primaryLight,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: theme.colors.textInverse,
    fontSize: fs(16),
    lineHeight: fs(20),
    marginLeft: s(8),
    fontFamily: GTFonts.semibold,
  },
  stickyButtonContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingTop: v(5),
    paddingBottom: v(12),
    borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderTopColor: theme.colors.borderGray200,
    ...(Platform.OS === 'web' && {
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      alignSelf: 'center',
      width: '100%',
      paddingHorizontal: s(24),
      borderTopWidth: 0,
    }),
  },
});

export default BusinessDetailsScreen;
